/*
 * tbw-carousel.js - tiny vanilla replacement for Slick / Swiper (CRO Sep 2026)
 *
 * The element with [data-tbw-carousel] is the scroll container; its element children are the
 * slides. Swiping/trackpad scrolling is native (CSS scroll-snap, see tbw-carousel.css), this file
 * only adds dots, arrows, autoplay, mouse drag and filtering.
 *
 * Options (JSON in the attribute value, all optional):
 *   perView   number | { "<min-width px>": number }   slides visible, fractions allowed (1.2)
 *   gap       number | { "<min-width px>": number }   px between slides
 *   dots      boolean            page dots rendered after the track (default false)
 *   autoplay  number             ms between slides; stops for good after the shopper interacts
 *   loop      boolean            autoplay/arrows wrap from last to first (default true)
 *   prev/next CSS selector       arrow elements, looked up inside the closest shopify-section
 *
 * API: element.tbwCarousel.{ next(), prev(), goTo(i), filter(selector|null), refresh() }
 * Global: window.TbwCarousel.init(root = document)
 */
(function () {
  if (window.TbwCarousel) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function responsive(value, fallback) {
    if (value == null) return fallback;
    if (typeof value === 'number') return value;
    var result = fallback;
    Object.keys(value)
      .map(Number)
      .sort(function (a, b) { return a - b; })
      .forEach(function (min) {
        if (window.innerWidth >= min) result = value[min];
      });
    return result;
  }

  function Carousel(el) {
    this.el = el;
    try {
      this.opts = JSON.parse(el.getAttribute('data-tbw-carousel') || '{}') || {};
    } catch (e) {
      this.opts = {};
    }
    this.loop = this.opts.loop !== false;
    this.index = 0;
    this.stopped = false;

    el.classList.add('tbw-carousel');
    el.setAttribute('tabindex', el.getAttribute('tabindex') || '0');
    if (!el.hasAttribute('aria-roledescription')) el.setAttribute('aria-roledescription', 'carousel');

    this.scope = el.closest('.shopify-section') || el.parentElement || document;
    if (this.opts.prev) this.prevEl = this.scope.querySelector(this.opts.prev);
    if (this.opts.next) this.nextEl = this.scope.querySelector(this.opts.next);

    if (this.opts.dots) {
      this.dotsEl = document.createElement('ul');
      this.dotsEl.className = 'tbw-carousel__dots';
      el.insertAdjacentElement('afterend', this.dotsEl);
    }

    this.bind();
    this.refresh();
    this.setupAutoplay();
  }

  Carousel.prototype.slides = function () {
    return Array.prototype.filter.call(this.el.children, function (child) {
      return !child.hidden && child.getClientRects().length > 0;
    });
  };

  Carousel.prototype.step = function () {
    var slides = this.slides();
    if (slides.length < 2) return slides[0] ? slides[0].getBoundingClientRect().width : this.el.clientWidth;
    return slides[1].offsetLeft - slides[0].offsetLeft;
  };

  Carousel.prototype.pageCount = function () {
    var count = this.slides().length;
    return Math.max(1, Math.ceil(count - this.perView + 1));
  };

  Carousel.prototype.refresh = function () {
    this.perView = responsive(this.opts.perView, 1);
    this.el.style.setProperty('--tbw-per-view', this.perView);
    this.el.style.setProperty('--tbw-gap', responsive(this.opts.gap, 0) + 'px');
    this.renderDots();
    this.update();
  };

  Carousel.prototype.renderDots = function () {
    if (!this.dotsEl) return;
    var pages = this.pageCount();
    this.dotsEl.hidden = pages < 2;
    if (this.dotsEl.children.length === pages) return;
    this.dotsEl.innerHTML = '';
    for (var i = 0; i < pages; i++) {
      var li = document.createElement('li');
      var button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      button.addEventListener('click', this.goTo.bind(this, i, true));
      li.appendChild(button);
      this.dotsEl.appendChild(li);
    }
  };

  Carousel.prototype.update = function () {
    var step = this.step() || 1;
    var pages = this.pageCount();
    var maxScroll = this.el.scrollWidth - this.el.clientWidth;
    var index = Math.round(this.el.scrollLeft / step);
    if (this.el.scrollLeft >= maxScroll - 2) index = pages - 1;
    this.index = Math.max(0, Math.min(pages - 1, index));

    if (this.dotsEl) {
      Array.prototype.forEach.call(
        this.dotsEl.children,
        function (li, i) {
          var active = i === this.index;
          li.classList.toggle('is-active', active);
          li.firstChild.setAttribute('aria-current', active ? 'true' : 'false');
        }.bind(this)
      );
    }
    if (!this.loop) {
      if (this.prevEl) this.prevEl.classList.toggle('is-disabled', this.index === 0);
      if (this.nextEl) this.nextEl.classList.toggle('is-disabled', this.index >= pages - 1);
    }
  };

  Carousel.prototype.goTo = function (index, byUser) {
    if (byUser) this.stop();
    var pages = this.pageCount();
    if (index >= pages) index = this.loop ? 0 : pages - 1;
    if (index < 0) index = this.loop ? pages - 1 : 0;
    this.el.scrollTo({ left: index * this.step(), behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  Carousel.prototype.next = function (byUser) {
    this.goTo(this.index + 1, byUser);
  };

  Carousel.prototype.prev = function (byUser) {
    this.goTo(this.index - 1, byUser);
  };

  /* Show only slides matching selector (null = all), like slick's slickFilter. */
  Carousel.prototype.filter = function (selector) {
    Array.prototype.forEach.call(this.el.children, function (child) {
      child.hidden = !!selector && !child.matches(selector);
    });
    this.el.scrollLeft = 0;
    this.refresh();
  };

  Carousel.prototype.bind = function () {
    var self = this;
    var ticking = false;

    this.el.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          self.update();
        });
      },
      { passive: true }
    );

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { self.refresh(); }, 150);
    });

    [
      [this.prevEl, function () { self.prev(true); }],
      [this.nextEl, function () { self.next(true); }]
    ].forEach(function (pair) {
      var el = pair[0];
      if (!el) return;
      el.addEventListener('click', function (e) { e.preventDefault(); pair[1](); });
      // Arrows are often <div>s in this theme - make them keyboard operable too.
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pair[1](); }
      });
    });

    this.el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.next(true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.prev(true); }
    });

    // Touch/trackpad scrolling is native; any of these means the shopper took over.
    ['touchstart', 'wheel'].forEach(function (type) {
      self.el.addEventListener(type, function () { self.stop(); }, { passive: true });
    });

    // Mouse drag (Slick's "draggable") - touch devices use native scrolling instead.
    var drag = null;
    this.el.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: self.el.scrollLeft, moved: false };
      self.stop();
    });
    window.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 5) return;
      if (!drag.moved) {
        drag.moved = true;
        self.el.classList.add('is-dragging');
      }
      self.el.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!drag) return;
      var moved = drag.moved;
      drag = null;
      self.el.classList.remove('is-dragging');
      if (moved) {
        // Let the snap settle on the nearest slide, and swallow the click that ends the drag.
        self.goTo(Math.round(self.el.scrollLeft / (self.step() || 1)));
        self.el.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); }, { capture: true, once: true });
      }
    });
  };

  Carousel.prototype.setupAutoplay = function () {
    var self = this;
    var delay = Number(this.opts.autoplay) || 0;
    if (!delay || reduceMotion) return;

    this.visible = false;
    this.hovered = false;
    this.el.addEventListener('mouseenter', function () { self.hovered = true; });
    this.el.addEventListener('mouseleave', function () { self.hovered = false; });
    this.el.addEventListener('focusin', function () { self.hovered = true; });
    this.el.addEventListener('focusout', function () { self.hovered = false; });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
      }).observe(this.el);
    } else {
      this.visible = true;
    }

    this.timer = setInterval(function () {
      if (self.stopped || !self.visible || self.hovered || document.hidden) return;
      if (self.pageCount() < 2) return;
      self.next(false);
    }, delay);
  };

  Carousel.prototype.stop = function () {
    this.stopped = true;
    if (this.timer) clearInterval(this.timer);
  };

  function init(root) {
    (root || document).querySelectorAll('[data-tbw-carousel]').forEach(function (el) {
      if (!el.tbwCarousel) el.tbwCarousel = new Carousel(el);
    });
  }

  window.TbwCarousel = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
